/**
 * Auth Guard Component
 *
 * Replaces Next.js middleware for auth protection.
 * Wraps protected routes and redirects to login if no session.
 *
 * Set VITE_AUTH_BYPASS=true in .env.staging to skip auth checks
 * and render protected routes with a mock user (for testing only).
 */

import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";

const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === "true";

export function AuthGuard() {
  const [session, setSession] = useState<Session | null | undefined>(
    AUTH_BYPASS ? null : undefined,
  );
  const location = useLocation();

  useEffect(() => {
    if (AUTH_BYPASS) return;

    // Check initial session
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    // Listen for auth state changes (login, logout, token refresh)
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      },
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Auth bypass — skip all checks, render children immediately
  if (AUTH_BYPASS) {
    return <Outlet />;
  }

  // Loading state while checking session
  if (session === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-center space-y-4">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"
            role="status"
          />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // No session — redirect to login
  if (session === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated — render child routes
  return <Outlet />;
}
